import { useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  SEARCH_COOPERATION_WAREHOUSES_FOR_APPOINTMENT,
  GET_COOPERATION_WAREHOUSE_FOR_APPOINTMENT,
  CooperationWarehouseForAppointment,
  SearchCooperationWarehousesForAppointmentResponse,
  SearchCooperationWarehousesForAppointmentVariables,
  GetCooperationWarehouseByIdResponse,
  GetCooperationWarehouseByIdVariables
} from '../graphql/queries/getCooperationWarehouseQueries';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";

export const useCooperationWarehouseSearch = () => {
  const [options, setOptions] = useState<CooperationWarehouseForAppointment[]>([]);
  const [loading, setLoading] = useState(false);
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id!;

  const [searchCooperationWarehouses] = useLazyQuery<
    SearchCooperationWarehousesForAppointmentResponse,
    SearchCooperationWarehousesForAppointmentVariables
  >(
    SEARCH_COOPERATION_WAREHOUSES_FOR_APPOINTMENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setOptions(data.searchCooperationWarehousesForAppointment || []);
        setLoading(false);
      },
      onError: (error) => {
        console.error('Error searching Pickup Sites:', error);
        setOptions([]);
        setLoading(false);
      }
    }
  );

  const [getCooperationWarehouseById] = useLazyQuery<
    GetCooperationWarehouseByIdResponse,
    GetCooperationWarehouseByIdVariables
  >(
      GET_COOPERATION_WAREHOUSE_FOR_APPOINTMENT,
    {
      fetchPolicy: 'cache-and-network',
      onError: (error) => {
        console.error('Error getting Pickup Site by id:', error);
      }
    }
  );

  const search = useCallback((name: string) => {
    if (name && name.trim().length > 0) {
      setLoading(true);
      searchCooperationWarehouses({
        variables: {
          name: name.trim(),
          warehouseId: warehouseId
        }
      });
    } else {
      setOptions([]);
      setLoading(false);
    }
  }, [searchCooperationWarehouses, warehouseId]);

  const reset = useCallback(() => {
    setOptions([]);
    setLoading(false);
  }, []);

  const loadWarehouseById = useCallback((cooperationWarehouseId: number) => {
    if (cooperationWarehouseId) {
      getCooperationWarehouseById({
        variables: { cooperationWarehouseId }
      });
    }
  }, [getCooperationWarehouseById]);

  const getWarehouseById = useCallback((cooperationWarehouseId: number) => {
    return options.find(w => w.cooperationWarehouseId === cooperationWarehouseId);
  }, [options]);

  return {
    options,
    loading,
    search,
    reset,
    loadWarehouseById,
    getWarehouseById
  };
};